import React, { useState, useCallback, useMemo, useEffect, memo } from "react";
import PropTypes from "prop-types";
import { useCommentsAndLocations } from "../../../hooks/commentsAndResidenceQuery";
import { useLayout } from "../../../context/LayoutContext";
import {
  useNotification,
  NotificationSeverity,
} from "../../../context/NotificationContext";
import LoadingAnimation from "../../../components/loading/LoadingAnimation";

import MultiDownloadModal from "../../../components/ui/modals/MultiDownloadModal";
import PreviewModal from "../../../components/ui/modals/PreviewModal";
import ConfirmDeleteModal from "../../../components/ui/modals/ConfirmDeleteModal";
import CommentsLocationsDrawer from "../../../components/ui/drawner/CommentsLocationsDrawer";
import { useQueryClient } from "@tanstack/react-query";
import { useVehicleProfile } from "../../../hooks/useVehicleQuery";
import VehicleProfileInfo from "../VehicleProfileInfo";
import VehicleProfileEdit from "../VehicleProfileEdit";
import {
  useDocumentationAnalytics,
  useDocumentTypes,
} from "../../../hooks/useDocumentationVehicleQuery";
import VehicleDocumentsSectionProfile from "../../../components/ui/tables/VehicleDocumentsSectionProfile";
import VehicleDocumentFilters from "../../../components/filters/VehicleDocumentFilters";
import { useDownload } from "../../../context/DownloadContext";

// Iconos para el toggle de vista
import GridViewIcon from "../../../assets/svg/view/grid_view.svg";
import GridViewLightIcon from "../../../assets/svg/view/grid_view_light.svg";
import SplitViewIcon from "../../../assets/svg/view/split_view.svg";
import SplitViewLightIcon from "../../../assets/svg/view/split_view_light.svg";

import "../../../assets/scss/views/VehicleProfile.scss";

// Constante para documentos que no expiran
export const NON_EXPIRABLE_VEHICLE_DOCUMENTS = [
  "Tarjeta de propiedad",
  "Registro fotográfico",
];

const MemoizedVehicleProfileInfo = memo(VehicleProfileInfo);
const MemoizedVehicleProfileEdit = memo(VehicleProfileEdit);
const MemoizedVehicleDocumentFilters = memo(VehicleDocumentFilters);
const MemoizedVehicleDocumentsSectionProfile = memo(
  VehicleDocumentsSectionProfile
);
const MemoizedPreviewModal = memo(PreviewModal);
const MemoizedMultiDownloadModal = memo(MultiDownloadModal);
const MemoizedConfirmDeleteModal = memo(ConfirmDeleteModal);
const MemoizedCommentsLocationsDrawer = memo(CommentsLocationsDrawer);

function VehicleProfile({ id }) {
  const { theme } = useLayout();
  const { showNotification } = useNotification();
  const { handleDownloadMultipleDocuments } = useDownload();
  const queryClient = useQueryClient();
  const { useOperationRequirements } = useDocumentationAnalytics();

  // Estados principales
  const [isEditMode, setIsEditMode] = useState(false);

  // Sistema de vista dual/simple
  const [viewMode, setViewMode] = useState("dual"); // 'simple' o 'dual'

  // Para modo simple (compatibilidad hacia atrás)
  const [currentView, setCurrentView] = useState("info"); // 'info' o 'documents'
  const [selectedVehicleType, setSelectedVehicleType] = useState("main"); // 'main' o 'related'
  const [downloadContext, setDownloadContext] = useState({
    vehicleType: "main", // "main" o "related"
    panelType: "simple", // "simple", "left", "right"
  });

  // Para modo dual - paneles independientes
  const [leftPanel, setLeftPanel] = useState({
    view: "info",
    vehicleType: "main",
  });
  const [rightPanel, setRightPanel] = useState({
    view: "documents",
    vehicleType: "main",
  });

  // Estados para filtrado y búsqueda
  const [searchValue, setSearchValue] = useState("");
  const [filterValue, setFilterValue] = useState("all");

  // Estados para modales
  const [openPreview, setOpenPreview] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [openMultiDownload, setOpenMultiDownload] = useState(false);
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [docToDelete, setDocToDelete] = useState(null);

  // Estado para tipos de documentos
  const [documentTypes, setDocumentTypes] = useState([]);

  // Hook principal de vehículo
  const {
    vehicleData,
    isLoading,
    isError,
    error,
    uploadDocument,
    updateDocument,
    deleteDocument,
    //downloadDocument,
    //downloadMultipleDocuments,
    selectedDocumentId,
    selectDocument,
    updateVehicleInfo,
  } = useVehicleProfile(id, {
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  // Determinar si el vehículo requiere trailer/cabezote
  const vehicleRequiresRelated = useMemo(() => {
    if (!vehicleData) return false;
    const typesWithTrailer = ["TRACTOCAMION", "CAMA ALTA", "CARGADOR"];
    return (
      typesWithTrailer.includes(vehicleData.type_name?.toUpperCase()) ||
      vehicleData.is_trailer
    );
  }, [vehicleData]);

  // Determinar si es un vehículo simple (sin trailer/cabezote)
  const isSimpleVehicle = useMemo(() => {
    if (!vehicleData) return false;
    const typesWithTrailer = ["TRACTOCAMION", "CAMA ALTA", "CARGADOR"];
    return (
      !typesWithTrailer.includes(vehicleData.type_name?.toUpperCase()) &&
      !vehicleData.is_trailer
    );
  }, [vehicleData]);

  // Determinar si tiene vehículo relacionado
  const hasRelatedVehicle = useMemo(() => {
    return vehicleData?.relatedVehicle && vehicleData.relatedVehicle.data;
  }, [vehicleData]);

  // Obtener el tipo de vehículo actual para mostrar en la UI
  const getCurrentVehicleTypeLabel = useCallback(
    (vehicleType) => {
      if (!vehicleData) return "";

      if (vehicleType === "main") {
        return vehicleData.is_trailer ? "Trailer" : "Cabezote";
      } else if (vehicleType === "related" && hasRelatedVehicle) {
        return vehicleData.relatedVehicle.type === "trailer"
          ? "Trailer"
          : "Cabezote";
      }

      return "";
    },
    [vehicleData, hasRelatedVehicle]
  );

  // Obtener el tipo de vehículo relacionado para mostrar en la UI
  const getRelatedVehicleTypeLabel = useMemo(() => {
    if (!vehicleData || !hasRelatedVehicle) return "";

    return vehicleData.relatedVehicle.type === "trailer"
      ? "Trailer"
      : "Cabezote";
  }, [vehicleData, hasRelatedVehicle]);

  // Queries dependientes (código existente)
  const {
    data: operationData,
    isLoading: isLoadingOperation,
    isError: isErrorOperation,
  } = useOperationRequirements(
    vehicleData?.belongs_to_operation && vehicleData?.operation_id
      ? vehicleData.operation_id
      : null,
    {
      enabled:
        !!vehicleData?.belongs_to_operation && !!vehicleData?.operation_id,
      staleTime: 10 * 60 * 1000,
    }
  );

  const { data: docTypesData, isLoading: isLoadingDocTypes } = useDocumentTypes(
    {
      vehicleDocuments: true,
    },
    {
      staleTime: 30 * 60 * 1000,
      cacheTime: 60 * 60 * 1000,
    }
  );

  // Hook de comentarios
  const {
    useCommentsByReference,
    createComment,
    updateComment,
    deleteComment,
  } = useCommentsAndLocations();

  const { data: documentComments } = useCommentsByReference(
    selectedDocumentId || "",
    {
      enabled: !!selectedDocumentId,
      staleTime: 2 * 60 * 1000,
    }
  );

  // Efecto para determinar tipos de documentos (código existente)
  useEffect(() => {
    if (!vehicleData) return;

    let newDocumentTypes = [];

    if (
      vehicleData.belongs_to_operation &&
      operationData?.data?.vehicleRequirements
    ) {
      // Lógica existente para operaciones...
      const { vehicleRequirements } = operationData.data;
      const requirements = vehicleRequirements.find(
        (req) => req.vehicleType.id === vehicleData.vehicle_type_id
      );

      if (requirements?.requiredDocuments) {
        newDocumentTypes = requirements.requiredDocuments;
      } else {
        newDocumentTypes = vehicleData.requiredDocuments || [];
      }
    } else if (docTypesData?.data) {
      newDocumentTypes = docTypesData.data;
    }

    if (JSON.stringify(newDocumentTypes) !== JSON.stringify(documentTypes)) {
      setDocumentTypes(newDocumentTypes);
    }
  }, [vehicleData, operationData?.data, docTypesData?.data]);
  // Función para obtener tipos de documentos según el panel
  const getDocumentTypesForPanel = useCallback(
    (panelVehicleType) => {
      if (!vehicleData) return [];

      if (panelVehicleType === "main") {
        // Para vehículo principal, usar la lógica existente
        if (
          vehicleData.belongs_to_operation &&
          operationData?.data?.vehicleRequirements
        ) {
          const { vehicleRequirements } = operationData.data;
          const requirements = vehicleRequirements.find(
            (req) => req.vehicleType.id === vehicleData.vehicle_type_id
          );

          if (requirements?.requiredDocuments) {
            return requirements.requiredDocuments;
          } else {
            return vehicleData.requiredDocuments || [];
          }
        } else {
          return vehicleData.requiredDocuments || docTypesData?.data || [];
        }
      } else if (panelVehicleType === "related" && vehicleData.relatedVehicle) {
        // Para vehículo relacionado, usar los documentos específicos
        if (
          vehicleData.relatedVehicle.data.belongs_to_operation &&
          operationData?.data?.vehicleRequirements
        ) {
          const { vehicleRequirements } = operationData.data;
          const requirements = vehicleRequirements.find(
            (req) =>
              req.vehicleType.id ===
              vehicleData.relatedVehicle.data.vehicle_type_id
          );

          if (requirements?.requiredDocuments) {
            return requirements.requiredDocuments;
          } else {
            return vehicleData.requiredDocumentsRelatedVehicle || [];
          }
        } else {
          return vehicleData.requiredDocumentsRelatedVehicle || [];
        }
      }

      return [];
    },
    [vehicleData, operationData?.data, docTypesData?.data]
  );

  // Handlers para vista y navegación
  const handleToggleViewMode = useCallback(() => {
    setViewMode((prevMode) => {
      const newMode = prevMode === "simple" ? "dual" : "simple";

      // Sincronizar con paneles al cambiar a dual - SIMPLIFICADO
      if (newMode === "dual") {
        // Panel izquierdo siempre info, panel derecho siempre documentos
        setLeftPanel({
          view: "info",
          vehicleType: selectedVehicleType,
        });

        setRightPanel({
          view: "documents",
          vehicleType: selectedVehicleType,
        });
      }

      return newMode;
    });
  }, [selectedVehicleType]);

  const handleSimpleNavigation = useCallback((view, vehicleType) => {
    setCurrentView(view);
    setSelectedVehicleType(vehicleType);
  }, []);

  const handleDualNavigation = useCallback((panel, vehicleType) => {
    if (panel === "left") {
      setLeftPanel({
        view: "info", // Siempre info
        vehicleType,
      });
    } else {
      setRightPanel({
        view: "documents", // Siempre documentos
        vehicleType,
      });
    }
  }, []);

  // Normalización de documentos (código existente)
  const normalizeDocuments = useMemo(() => {
    return (documents = []) =>
      documents.map((doc) => ({
        ...doc,
        document_type: {
          id: doc.document_type_id,
          name: doc.document_type_name,
          description: doc.document_type_description || "",
        },
      }));
  }, []);

  // Obtener documentos según el panel especificado
  const getDocumentsForPanel = useCallback(
    (panelVehicleType) => {
      if (!vehicleData) return [];

      if (panelVehicleType === "main") {
        return vehicleData.documents || [];
      } else if (panelVehicleType === "related" && vehicleData.relatedVehicle) {
        return vehicleData.relatedVehicle.data.documents || [];
      }

      return [];
    },
    [vehicleData]
  );

  // Filtrado de documentos para modo simple (compatible con código existente)
  const getCurrentDocuments = useMemo(() => {
    return getDocumentsForPanel(selectedVehicleType);
  }, [getDocumentsForPanel, selectedVehicleType]);

  // Filtrado de documentos (código existente adaptado)
  const filteredData = useMemo(() => {
    const documentsToFilter = getCurrentDocuments;
    const currentDocumentTypes = getDocumentTypesForPanel(selectedVehicleType);

    if (!documentsToFilter) return { documents: [], types: [] };

    let filtered = normalizeDocuments(documentsToFilter);
    let relevantTypes = [...(documentTypes || [])].map((type) => ({
      id: type.id,
      name: type.name,
      description: type.description || "",
    }));

    // Aplicar búsqueda
    if (searchValue.trim()) {
      const searchLower = searchValue.toLowerCase();

      filtered = filtered.filter(
        (doc) =>
          doc.original_name_description?.toLowerCase().includes(searchLower) ||
          doc.file_name?.toLowerCase().includes(searchLower) ||
          doc.document_type?.name?.toLowerCase().includes(searchLower) ||
          doc.document_type_name?.toLowerCase().includes(searchLower)
      );

      relevantTypes = relevantTypes.filter(
        (type) =>
          filtered.some(
            (doc) =>
              doc.document_type?.id === type.id ||
              doc.document_type_id === type.id
          ) || type.name?.toLowerCase().includes(searchLower)
      );
    }

    // Aplicar filtros (código existente)
    switch (filterValue) {
      case "expiration": {
        const now = new Date();
        const thirtyDaysFromNow = new Date();
        thirtyDaysFromNow.setDate(now.getDate() + 30);

        filtered = filtered.filter((doc) => {
          if (
            NON_EXPIRABLE_VEHICLE_DOCUMENTS.includes(doc.document_type.name)
          ) {
            return false;
          }

          if (!doc.expiration_date) return false;
          const expirationDate = new Date(doc.expiration_date);
          return expirationDate <= thirtyDaysFromNow;
        });

        relevantTypes = relevantTypes.filter((type) =>
          filtered.some((doc) => doc.document_type.id === type.id)
        );
        break;
      }

      case "empty":
        const emptyTypes =
          documentTypes?.filter((type) => {
            const docsOfThisType = documentsToFilter.filter(
              (doc) => doc.document_type_id === type.id
            );

            if (docsOfThisType.length === 0) return true;

            const allDocsAreTemporary = docsOfThisType.every(
              (doc) => doc.id?.startsWith("temp-") || doc.isLocal === true
            );

            return allDocsAreTemporary;
          }) || [];

        filtered = documentsToFilter.filter((doc) => {
          const isTemporary =
            doc.id?.startsWith("temp-") || doc.isLocal === true;
          const belongsToEmptyType = emptyTypes.some(
            (type) => type.id === doc.document_type_id
          );

          return isTemporary && belongsToEmptyType;
        });

        relevantTypes = emptyTypes.map((type) => ({
          id: type.id,
          name: type.name,
          description: type.description || "",
        }));
        break;

      case "required":
        filtered = filtered.filter((doc) => doc.required);
        relevantTypes = relevantTypes.filter((type) =>
          filtered.some((doc) => doc.document_type.id === type.id)
        );
        break;

      case "not-required":
        filtered = filtered.filter((doc) => !doc.required);
        relevantTypes = relevantTypes.filter((type) =>
          filtered.some((doc) => doc.document_type.id === type.id)
        );
        break;

      default:
        break;
    }

    return {
      documents: filtered,
      types: relevantTypes,
    };
  }, [
    getCurrentDocuments,
    getDocumentTypesForPanel,
    selectedVehicleType,
    searchValue,
    filterValue,
    normalizeDocuments,
  ]);

  // Handlers para documentos (código existente)
  const handleEditClick = useCallback(() => {
    setIsEditMode(true);
  }, []);

  const handleCancelEdit = useCallback(() => {
    setIsEditMode(false);
  }, []);
  const handleSaveEdit = useCallback(
  async (updatedData, isRelatedVehicle = false) => {
    try {
      let targetVehicleId = id; 

      // Determinar ID del vehículo a actualizar
      if (isRelatedVehicle && vehicleData?.relatedVehicle?.data?.id) {
        targetVehicleId = vehicleData.relatedVehicle.data.id;
      }

      console.log("🎯 Actualizando vehículo:", {
        isRelatedVehicle,
        targetVehicleId,
        mainVehicleId: id,
        relatedVehicleId: vehicleData?.relatedVehicle?.data?.id
      });

      // Llamar a la actualización con el ID correcto
      await updateVehicleInfo(updatedData, targetVehicleId);
      
      setIsEditMode(false);
      showNotification({
        message: "Información del vehículo actualizada correctamente",
        severity: NotificationSeverity.SUCCESS,
      });
    } catch (error) {
      console.error("❌ Error al actualizar:", error);
      showNotification({
        message: "Error al actualizar la información del vehículo",
        severity: NotificationSeverity.ERROR,
      });
    }
  },
  [updateVehicleInfo, id, vehicleData, showNotification]
);

  // Resto de handlers existentes (código existente - no cambió)
  const handlePreviewDocument = useCallback((doc) => {
    setSelectedDoc(doc);
    setOpenPreview(true);
  }, []);

  const handleUpdateDocument = useCallback(
    async (doc) => {
      try {
        const updatePayload = {
          docType: doc.document_type?.id || doc.document_type_id,
          description: doc.original_name_description || "",
          required: doc.required || false,
          expeditionDate: doc.expedition_date,
          expirationDate: doc.expiration_date,
        };

        if (doc.file && doc.file instanceof File) {
          updatePayload.file = doc.file;
          updatePayload.fileName = doc.file.name;
        }

        const currentCache = queryClient.getQueryData(["vehicleProfile", id]);
        if (currentCache?.data?.documents) {
          const optimisticDocs = currentCache.data.documents.map(
            (existingDoc) =>
              existingDoc.id === doc.id
                ? { ...existingDoc, ...updatePayload, updating: true }
                : existingDoc
          );

          queryClient.setQueryData(["vehicleProfile", id], {
            ...currentCache,
            data: {
              ...currentCache.data,
              documents: optimisticDocs,
            },
          });
        }

        await updateDocument(doc.id, updatePayload);
        queryClient.invalidateQueries(["vehicleProfile", id]);

        showNotification({
          message: "Documento actualizado correctamente",
          severity: NotificationSeverity.SUCCESS,
        });
      } catch (err) {
        console.error("Error actualizando documento:", err);
        queryClient.invalidateQueries(["vehicleProfile", id]);

        showNotification({
          message:
            "Error al actualizar el documento: " +
            (err.message || "Error desconocido"),
          severity: NotificationSeverity.ERROR,
        });
      }
    },
    [updateDocument, queryClient, id, showNotification]
  );

  const handleDeleteDocument = useCallback((doc) => {
    setDocToDelete(doc);
    setOpenDeleteDialog(true);
  }, []);

  const confirmDeleteDocument = useCallback(async () => {
    if (!docToDelete) return;

    try {
      const currentCache = queryClient.getQueryData(["vehicleProfile", id]);
      if (currentCache?.data?.documents) {
        const filteredDocs = currentCache.data.documents.filter(
          (doc) => doc.id !== docToDelete.id
        );

        queryClient.setQueryData(["vehicleProfile", id], {
          ...currentCache,
          data: {
            ...currentCache.data,
            documents: filteredDocs,
          },
        });
      }

      await deleteDocument(docToDelete.id);
      queryClient.invalidateQueries(["vehicleProfile", id]);

      showNotification({
        message: "Documento eliminado correctamente",
        severity: NotificationSeverity.SUCCESS,
      });
    } catch (error) {
      console.error("Error eliminando documento:", error);
      queryClient.invalidateQueries(["vehicleProfile", id]);

      showNotification({
        message:
          "Error al eliminar el documento: " +
          (error.message || "Error desconocido"),
        severity: NotificationSeverity.ERROR,
      });
    } finally {
      setOpenDeleteDialog(false);
      setDocToDelete(null);
    }
  }, [docToDelete, deleteDocument, queryClient, id, showNotification]);

  // Resto de handlers existentes para documentos y comentarios (sin cambios)
  const handleAddDocument = useCallback(
  async (newDoc) => {
    try {
      // Determinar el vehículo de destino según el contexto
      const targetVehicleType = newDoc.vehicleContext?.type || "main";
      const targetVehicleId = newDoc.vehicleContext?.vehicleId || id;
      
      console.log("🎯 Agregando documento a:", {
        targetVehicleType,
        targetVehicleId,
        docType: newDoc.document_type_name
      });

      if (newDoc.isNew || newDoc.isLocal || newDoc.id?.startsWith("temp-")) {
        // Determinar la clave de cache correcta
        const cacheKey = ["vehicleProfile", id]; // Siempre usar el ID principal para el cache
        const currentCache = queryClient.getQueryData(cacheKey) || {
          data: { documents: [] },
        };

        const normalizedDoc = {
          ...newDoc,
          document_type_id: newDoc.document_type_id,
          document_type_name: newDoc.document_type_name || "",
          expedition_date: newDoc.expedition_date || null,
          expiration_date: newDoc.expiration_date || null,
          original_name_description: newDoc.original_name_description || "",
          required: !!newDoc.required,
          file_name: newDoc.file_name || "",
          isTemporary: true,
          // Mantener el contexto del vehículo
          targetVehicleType: targetVehicleType,
        };

        // Agregar el documento al array correcto según el tipo de vehículo
        let updatedCache;
        if (targetVehicleType === "main") {
          const existingDocs = Array.isArray(currentCache.data?.documents)
            ? [...currentCache.data.documents]
            : [];

          updatedCache = {
            ...currentCache,
            data: {
              ...currentCache.data,
              documents: [...existingDocs, normalizedDoc],
            },
          };
        } else if (targetVehicleType === "related") {
          const existingRelatedDocs = Array.isArray(
            currentCache.data?.relatedVehicle?.data?.documents
          )
            ? [...currentCache.data.relatedVehicle.data.documents]
            : [];

          updatedCache = {
            ...currentCache,
            data: {
              ...currentCache.data,
              relatedVehicle: {
                ...currentCache.data.relatedVehicle,
                data: {
                  ...currentCache.data.relatedVehicle.data,
                  documents: [...existingRelatedDocs, normalizedDoc],
                },
              },
            },
          };
        }

        queryClient.setQueryData(cacheKey, updatedCache);

        if (!newDoc.file) return;
      }

      if (newDoc.file) {
        const uploadPayload = {
          docType: newDoc.document_type_id,
          description: newDoc.original_name_description || "",
          file: newDoc.file,
          fileName: newDoc.file.name,
          required: !!newDoc.required,
          expeditionDate: newDoc.expedition_date,
          expirationDate: newDoc.expiration_date,
          // Agregar el ID del vehículo de destino para el upload
          vehicleId: targetVehicleId,
        };

        // Si es un vehículo relacionado, usar el hook de upload con el ID correcto
        await uploadDocument(uploadPayload, targetVehicleId);
        await queryClient.invalidateQueries(["vehicleProfile", id]);

        showNotification({
          message: `Documento añadido correctamente al ${getCurrentVehicleTypeLabel(targetVehicleType)}`,
          severity: NotificationSeverity.SUCCESS,
        });
      }
    } catch (error) {
      console.error("Error al añadir documento:", error);
      queryClient.invalidateQueries(["vehicleProfile", id]);

      showNotification({
        message:
          "Error al añadir documento: " +
          (error.message || "Error desconocido"),
        severity: NotificationSeverity.ERROR,
      });
    }
  },
  [uploadDocument, queryClient, id, showNotification, getCurrentVehicleTypeLabel]
);


const handleDocumentChange = useCallback(
  (action) => {
    switch (action.type) {
      case "ADD_DOCUMENT":
        handleAddDocument(action.payload);
        break;

      case "UPDATE_DOCUMENT":
        if (vehicleData?.documents) {
          const { docId, field, value } = action.payload;
          const cacheKey = ["vehicleProfile", id];
          const currentCache = queryClient.getQueryData(cacheKey);

          if (currentCache?.data?.documents) {
            const updatedDocs = currentCache.data.documents.map((doc) =>
              doc.id === docId ? { ...doc, [field]: value } : doc
            );

            queryClient.setQueryData(cacheKey, {
              ...currentCache,
              data: {
                ...currentCache.data,
                documents: updatedDocs,
              },
            });
          }
        }
        break;

      case "DELETE_DOCUMENT":
        if (action.payload) {
          setDocToDelete(action.payload);
          setOpenDeleteDialog(true);
        } else if (docToDelete) {
          confirmDeleteDocument();
        }
        break;

      case "DELETE_LOCAL_DOCUMENT":
        const { docId } = action.payload;
        const cacheKey = ["vehicleProfile", id];
        const currentCache = queryClient.getQueryData(cacheKey);
        
        if (currentCache?.data) {
          // Crear una copia profunda del cache para evitar mutaciones
          let updatedCache = { ...currentCache };
          
          // Buscar y eliminar el documento de los documentos principales
          if (currentCache.data.documents) {
            const filteredMainDocs = currentCache.data.documents.filter(
              (doc) => doc.id !== docId
            );
            updatedCache.data = {
              ...updatedCache.data,
              documents: filteredMainDocs,
            };
          }
          
          // Buscar y eliminar el documento de los documentos del vehículo relacionado
          if (currentCache.data.relatedVehicle?.data?.documents) {
            const filteredRelatedDocs = currentCache.data.relatedVehicle.data.documents.filter(
              (doc) => doc.id !== docId
            );
            updatedCache.data = {
              ...updatedCache.data,
              relatedVehicle: {
                ...updatedCache.data.relatedVehicle,
                data: {
                  ...updatedCache.data.relatedVehicle.data,
                  documents: filteredRelatedDocs,
                },
              },
            };
          }
          
          // Actualizar el cache con los cambios aplicados
          queryClient.setQueryData(cacheKey, updatedCache);
        }
        break;

      case "VALIDATION_ERROR":
        showNotification({
          message: action.payload.message,
          severity: NotificationSeverity.WARNING,
        });
        break;

      default:
        console.warn("Acción no reconocida:", action.type);
    }
  },
  [
    handleAddDocument,
    docToDelete,
    confirmDeleteDocument,
    vehicleData,
    queryClient,
    id,
    showNotification,
  ]
);
  const handleOpenMultiDownload = useCallback(
    (vehicleType, panelType = "simple") => {
      console.log("🔍 Abriendo modal de descarga:", { vehicleType, panelType });

      // Resetear documentos seleccionados al cambiar contexto
      setSelectedDocs([]);

      // Establecer contexto de descarga
      setDownloadContext({ vehicleType, panelType });
      setOpenMultiDownload(true);
    },
    []
  );
  const prepareDocumentsForDownload = useCallback(
    (vehicleType = "main") => {
      let documentsToUse = [];
      let vehicleInfo = {};

      if (vehicleType === "main") {
        documentsToUse = vehicleData?.documents || [];
        vehicleInfo = {
          plate: vehicleData?.plate,
          type: vehicleData?.is_trailer ? "Trailer" : "Cabezote",
          isTrailer: vehicleData?.is_trailer,
        };
      } else if (vehicleType === "related" && vehicleData?.relatedVehicle) {
        documentsToUse = vehicleData.relatedVehicle.data.documents || [];
        vehicleInfo = {
          plate: vehicleData.relatedVehicle.data.plate,
          type:
            vehicleData.relatedVehicle.type === "trailer"
              ? "Trailer"
              : "Cabezote",
          isTrailer: vehicleData.relatedVehicle.type === "trailer",
        };
      }

      if (!documentsToUse.length) {
        return {
          vehicle: [],
          trailer: [],
          others: [],
          vehicleInfo,
        };
      }

      // Categorización inteligente basada en el tipo de vehículo actual
      const vehicleDocsTypes = [
        "TARJETA DE PROPIEDAD",
        "SOAT",
        "CERTIFICADO TECNOMECANICA",
        "POLIZA TODO RIESGO",
        "FOTO DEL VEHICULO",
      ];

      const trailerDocsTypes = [
        "KING PIN",
        "LINEA DE VIDA",
        "TABLA DE AFORO",
        "PRUEBA HIDROSTATICA",
        "CERTIFICADO QUINTA RUEDA",
        "PRUEBA DE ESPESORES",
        "FICHA TECNICA",
      ];

      const validDocuments = documentsToUse.filter(
        (doc) => doc.file_name && doc.file_path
      );

      const categorizedDocs = {
        vehicle: [],
        trailer: [],
        others: [],
        vehicleInfo,
      };

      validDocuments.forEach((doc) => {
        const docTypeName = doc.document_type_name?.toUpperCase() || "";
        if (vehicleDocsTypes.some((type) => docTypeName.includes(type))) {
          categorizedDocs.vehicle.push(doc);
        } else if (
          trailerDocsTypes.some((type) => docTypeName.includes(type))
        ) {
          categorizedDocs.trailer.push(doc);
        } else {
          categorizedDocs.others.push(doc);
        }
      });

      return categorizedDocs;
    },
    [vehicleData]
  );

  const handleDownloadAll = useCallback(async () => {
    if (selectedDocs.length === 0) {
      showNotification({
        message: "No hay documentos seleccionados para descargar",
        severity: NotificationSeverity.WARNING,
      });
      return;
    }

    try {
      await handleDownloadMultipleDocuments(selectedDocs, 2);

      showNotification({
        message: "Documentos descargados exitosamente",
        severity: NotificationSeverity.SUCCESS,
      });
    } catch (error) {
      console.error("Error al descargar documentos:", error);
      showNotification({
        message:
          "Error al descargar los documentos: " +
          (error.message || "Error desconocido"),
        severity: NotificationSeverity.ERROR,
      });
    }
  }, [selectedDocs, handleDownloadMultipleDocuments, showNotification]);

  const handleCreateComment = useCallback(
    async (commentData) => {
      if (!selectedDocumentId) {
        showNotification({
          message: "No se puede crear comentario: Documento no seleccionado",
          severity: NotificationSeverity.ERROR,
        });
        return;
      }

      try {
        const cacheKey = ["comments", selectedDocumentId];
        const currentComments = queryClient.getQueryData(cacheKey);

        const optimisticComment = {
          id: `temp-${Date.now()}`,
          content: commentData.content,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          reference_id: selectedDocumentId,
          isOptimistic: true,
          ...commentData,
        };

        if (currentComments?.data) {
          queryClient.setQueryData(cacheKey, {
            ...currentComments,
            data: [...currentComments.data, optimisticComment],
          });
        }

        await createComment({
          ...commentData,
          reference_id: selectedDocumentId,
        });

        queryClient.invalidateQueries(cacheKey);

        showNotification({
          message: "Comentario creado exitosamente",
          severity: NotificationSeverity.SUCCESS,
        });
      } catch (error) {
        console.error("Error al crear comentario:", error);
        queryClient.invalidateQueries(["comments", selectedDocumentId]);

        showNotification({
          message: error.message || "Error al crear comentario",
          severity: NotificationSeverity.ERROR,
        });
      }
    },
    [createComment, selectedDocumentId, queryClient, showNotification]
  );

  const handleUpdateComment = useCallback(
    async (commentId, commentData) => {
      if (!selectedDocumentId || !commentId) return;

      try {
        const cacheKey = ["comments", selectedDocumentId];
        const currentComments = queryClient.getQueryData(cacheKey);

        if (currentComments?.data) {
          const updatedComments = currentComments.data.map((comment) =>
            comment.id === commentId
              ? { ...comment, ...commentData, updating: true }
              : comment
          );

          queryClient.setQueryData(cacheKey, {
            ...currentComments,
            data: updatedComments,
          });
        }

        await updateComment({
          commentId,
          data: {
            ...commentData,
            reference_id: selectedDocumentId,
          },
        });

        queryClient.invalidateQueries(cacheKey);

        showNotification({
          message: "Comentario actualizado exitosamente",
          severity: NotificationSeverity.SUCCESS,
        });
      } catch (error) {
        console.error("Error al actualizar comentario:", error);
        queryClient.invalidateQueries(["comments", selectedDocumentId]);

        showNotification({
          message: error.message || "Error al actualizar comentario",
          severity: NotificationSeverity.ERROR,
        });
      }
    },
    [updateComment, selectedDocumentId, queryClient, showNotification]
  );

  const handleDeleteComment = useCallback(
    async (commentId) => {
      if (!selectedDocumentId || !commentId) return;

      try {
        const cacheKey = ["comments", selectedDocumentId];
        const currentComments = queryClient.getQueryData(cacheKey);

        if (currentComments?.data) {
          const filteredComments = currentComments.data.filter(
            (comment) => comment.id !== commentId
          );

          queryClient.setQueryData(cacheKey, {
            ...currentComments,
            data: filteredComments,
          });
        }

        await deleteComment(commentId);
        queryClient.invalidateQueries(cacheKey);

        showNotification({
          message: "Comentario eliminado exitosamente",
          severity: NotificationSeverity.SUCCESS,
        });
      } catch (error) {
        console.error("Error al eliminar comentario:", error);
        queryClient.invalidateQueries(["comments", selectedDocumentId]);

        showNotification({
          message: error.message || "Error al eliminar comentario",
          severity: NotificationSeverity.ERROR,
        });
      }
    },
    [deleteComment, selectedDocumentId, queryClient, showNotification]
  );
  // Función para renderizar contenido de panel de documenots (para dual)
  const getFilteredDocumentsForPanel = useCallback(
    (panelVehicleType) => {
      const documentsToFilter = getDocumentsForPanel(panelVehicleType);
      const panelDocumentTypes = getDocumentTypesForPanel(panelVehicleType);

      if (!documentsToFilter) return { documents: [], types: [] };

      let filtered = normalizeDocuments(documentsToFilter);
      let relevantTypes = [...(panelDocumentTypes || [])].map((type) => ({
        id: type.id,
        name: type.name,
        description: type.description || "",
      }));

      // Aplicar búsqueda
      if (searchValue.trim()) {
        const searchLower = searchValue.toLowerCase();

        filtered = filtered.filter(
          (doc) =>
            doc.original_name_description
              ?.toLowerCase()
              .includes(searchLower) ||
            doc.file_name?.toLowerCase().includes(searchLower) ||
            doc.document_type?.name?.toLowerCase().includes(searchLower) ||
            doc.document_type_name?.toLowerCase().includes(searchLower)
        );

        relevantTypes = relevantTypes.filter(
          (type) =>
            filtered.some(
              (doc) =>
                doc.document_type?.id === type.id ||
                doc.document_type_id === type.id
            ) || type.name?.toLowerCase().includes(searchLower)
        );
      }

      // Aplicar filtros (código existente sin cambios)
      switch (filterValue) {
        case "expiration": {
          const now = new Date();
          const thirtyDaysFromNow = new Date();
          thirtyDaysFromNow.setDate(now.getDate() + 30);

          filtered = filtered.filter((doc) => {
            if (
              NON_EXPIRABLE_VEHICLE_DOCUMENTS.includes(doc.document_type.name)
            ) {
              return false;
            }

            if (!doc.expiration_date) return false;
            const expirationDate = new Date(doc.expiration_date);
            return expirationDate <= thirtyDaysFromNow;
          });

          relevantTypes = relevantTypes.filter((type) =>
            filtered.some((doc) => doc.document_type.id === type.id)
          );
          break;
        }

        case "empty":
          const emptyTypes =
            panelDocumentTypes?.filter((type) => {
              const docsOfThisType = documentsToFilter.filter(
                (doc) => doc.document_type_id === type.id
              );

              if (docsOfThisType.length === 0) return true;

              const allDocsAreTemporary = docsOfThisType.every(
                (doc) => doc.id?.startsWith("temp-") || doc.isLocal === true
              );

              return allDocsAreTemporary;
            }) || [];

          filtered = documentsToFilter.filter((doc) => {
            const isTemporary =
              doc.id?.startsWith("temp-") || doc.isLocal === true;
            const belongsToEmptyType = emptyTypes.some(
              (type) => type.id === doc.document_type_id
            );

            return isTemporary && belongsToEmptyType;
          });

          relevantTypes = emptyTypes.map((type) => ({
            id: type.id,
            name: type.name,
            description: type.description || "",
          }));
          break;

        case "required":
          filtered = filtered.filter((doc) => doc.required);
          relevantTypes = relevantTypes.filter((type) =>
            filtered.some((doc) => doc.document_type.id === type.id)
          );
          break;

        case "not-required":
          filtered = filtered.filter((doc) => !doc.required);
          relevantTypes = relevantTypes.filter((type) =>
            filtered.some((doc) => doc.document_type.id === type.id)
          );
          break;

        default:
          break;
      }

      return {
        documents: filtered,
        types: relevantTypes,
      };
    },
    [
      getDocumentsForPanel,
      getDocumentTypesForPanel,
      normalizeDocuments,
      searchValue,
      filterValue,
    ]
  );
  // Función para renderizar contenido de panel
  const renderPanelContent = useCallback(
    (panelConfig, panelType = "full") => {
      const { view, vehicleType } = panelConfig;

      // Verificar si hay vehículo relacionado cuando se necesita
      if (
        vehicleRequiresRelated &&
        vehicleType === "related" &&
        !hasRelatedVehicle
      ) {
        return (
          <div className={`GM__${theme}-no-related-overlay`}>
            <div className={`GM__${theme}-no-related-message`}>
              <h3>
                Este {getCurrentVehicleTypeLabel("main")} no cuenta con su
                respectivo {vehicleData.is_trailer ? "Cabezote" : "Trailer"}.
                Muy pronto colocaremos para la integración del mismo.
              </h3>
            </div>
          </div>
        );
      }

      const currentVehicleData =
        vehicleType === "main" ? vehicleData : vehicleData.relatedVehicle?.data;

      if (view === "info") {
        return isEditMode ? (
          <MemoizedVehicleProfileEdit
            vehicle={currentVehicleData}
            theme={theme}
            onSave={(data) => handleSaveEdit(data, vehicleType === "related")}
            onCancel={handleCancelEdit}
            isRelatedVehicle={vehicleType === "related"}
          />
        ) : (
          <MemoizedVehicleProfileInfo
            vehicle={currentVehicleData}
            theme={theme}
            onEditClick={handleEditClick}
            showRelatedVehicle={
              !vehicleRequiresRelated && vehicleData.relatedVehicle
            }
            isSimpleVehicle={isSimpleVehicle}
          />
        );
      }
      if (view === "documents") {
        const panelDocuments = getDocumentsForPanel(vehicleType);
        const panelFilteredData = getFilteredDocumentsForPanel(vehicleType);
        const contextVehicleId = vehicleType === "main" ? id : vehicleData?.relatedVehicle?.data?.id;

        return (
          <>
            <div className={`GM__${theme}-multi-download-container`}>
              <MemoizedVehicleDocumentFilters
                documents={panelDocuments}
                onSearchChange={(e) => setSearchValue(e.target.value)}
                onFilterChange={(e) => setFilterValue(e.target.value)}
                searchValue={searchValue}
                filterValue={filterValue}
                onExpirationFilterClick={() => setFilterValue("expiration")}
                handleOpenMultiDownload={(vType, pType) => {
                  console.log("🎯 Filtros llamando descarga:", {
                    vType,
                    pType,
                    vehicleType,
                    panelType,
                  });
                  handleOpenMultiDownload(
                    vType || vehicleType,
                    pType || panelType
                  );
                }}
                theme={theme}
                vehicleType={vehicleType}
                panelType={panelType}
                currentVehicleInfo={{
                  plate:
                    vehicleType === "main"
                      ? vehicleData?.plate
                      : vehicleData?.relatedVehicle?.data?.plate,
                  type: getCurrentVehicleTypeLabel(vehicleType),
                }}
              />
            </div>

            <MemoizedVehicleDocumentsSectionProfile
              documents={panelFilteredData.documents}
              dataTypes={panelFilteredData.types}
              onDocumentsChange={handleDocumentChange}
              isLoading={isLoading}
              canEdit={!isEditMode}
              onPreview={handlePreviewDocument}
              onUpdate={handleUpdateDocument}
              onDelete={handleDeleteDocument}
              onAddDocument={handleAddDocument}
              theme={theme}
              vehicleContext={{
                type: vehicleType,
                vehicleId: contextVehicleId,
              }}
            />
          </>
        );
      }
      return null;
    },
    [
      vehicleRequiresRelated,
      hasRelatedVehicle,
      getCurrentVehicleTypeLabel,
      vehicleData,
      theme,
      isEditMode,
      handleSaveEdit,
      handleCancelEdit,
      handleEditClick,
      isSimpleVehicle,
      getFilteredDocumentsForPanel,
      getDocumentsForPanel,
      searchValue,
      filterValue,
      filteredData,
      handleDocumentChange,
      isLoading,
      handlePreviewDocument,
      handleUpdateDocument,
      handleDeleteDocument,
      handleAddDocument,
      handleOpenMultiDownload,
      getCurrentVehicleTypeLabel,
    ]
  );

  // Estados de carga y error
  if (isLoading || isLoadingOperation || isLoadingDocTypes)
    return <LoadingAnimation />;

  if (isError)
    return (
      <div className={`GM__${theme}-error-message`}>
        Error al cargar los datos del vehículo:{" "}
        {error?.message || "Error desconocido"}
      </div>
    );

  if (isErrorOperation)
    return (
      <div className={`GM__${theme}-error-message`}>
        Error al cargar los requisitos de operación. Se mostrarán documentos
        genéricos.
      </div>
    );

  if (!vehicleData)
    return (
      <div className={`GM__${theme}-error-message`}>
        No se encontró información del vehículo
      </div>
    );

  // Iconos para el toggle
  const gridViewIcon = theme === "dark" ? GridViewLightIcon : GridViewIcon;
  const splitViewIcon = theme === "dark" ? SplitViewLightIcon : SplitViewIcon;

  return (
<div className={`VMGP__${theme}-vehicle-profile ${viewMode}`}>
      {/* Header con toggle de vista */}
      <div className={`VMGP__${theme}-profile-header-controls`}>
        <div className={`VMGP__${theme}-view-mode-toggle`}>
          <button
            className={`VMGP__${theme}-view-toggle-btn`}
            onClick={handleToggleViewMode}
            title={viewMode === "simple" ? "Vista dividida" : "Vista simple"}
          >
            <img
              src={viewMode === "simple" ? splitViewIcon : gridViewIcon}
              alt={viewMode === "simple" ? "Vista dividida" : "Vista simple"}
              className={`VMGP__${theme}-view-icon`}
            />
          </button>
        </div>
      </div>

      {/* VISTA SIMPLE */}
      {viewMode === "simple" && (
        <>
          {/* Navegación de pestañas para vehículos simples */}
          {isSimpleVehicle && (
            <div className={`VMGP__${theme}-profile-navigation simple-vehicle`}>
              <button
                className={`VMGP__${theme}-nav-button ${
                  currentView === "info" ? "active" : ""
                }`}
                onClick={() => handleSimpleNavigation("info", "main")}
              >
                Datos básicos
              </button>
              <button
                className={`VMGP__${theme}-nav-button ${
                  currentView === "documents" ? "active" : ""
                }`}
                onClick={() => handleSimpleNavigation("documents", "main")}
              >
                Documentos
              </button>
            </div>
          )}

          {/* Navegación para vehículos con trailer/cabezote */}
          {vehicleRequiresRelated && (
            <div className={`VMGP__${theme}-profile-navigation`}>
              {/* Grupo de botones para Datos básicos */}
              <div className={`VMGP__${theme}-nav-group`}>
                <div className={`VMGP__${theme}-button-group`}>
                  <button
                    className={`VMGP__${theme}-nav-button ${
                      currentView === "info" && selectedVehicleType === "main"
                        ? "active"
                        : ""
                    } left`}
                    onClick={() => handleSimpleNavigation("info", "main")}
                  >
                    Datos básicos{" "}
                    <strong>{getCurrentVehicleTypeLabel("main")}</strong>
                  </button>
                  <button
                    className={`VMGP__${theme}-nav-button ${
                      currentView === "info" &&
                      selectedVehicleType === "related"
                        ? "active"
                        : ""
                    } right`}
                    onClick={() => handleSimpleNavigation("info", "related")}
                  >
                    Cambiar a{" "}
                    <strong>{getRelatedVehicleTypeLabel || "N/A"}</strong>
                  </button>
                </div>
              </div>

              {/* Grupo de botones para Documentos */}
              <div className={`VMGP__${theme}-nav-group`}>
                <div className={`VMGP__${theme}-button-group`}>
                  <button
                    className={`VMGP__${theme}-nav-button ${
                      currentView === "documents" &&
                      selectedVehicleType === "main"
                        ? "active"
                        : ""
                    } left`}
                    onClick={() => handleSimpleNavigation("documents", "main")}
                  >
                    Documentos{" "}
                    <strong>{getCurrentVehicleTypeLabel("main")}</strong>
                  </button>
                  <button
                    className={`VMGP__${theme}-nav-button ${
                      currentView === "documents" &&
                      selectedVehicleType === "related"
                        ? "active"
                        : ""
                    } right`}
                    onClick={() =>
                      handleSimpleNavigation("documents", "related")
                    }
                  >
                    Documentos{" "}
                    <strong>{getRelatedVehicleTypeLabel || "N/A"}</strong>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Contenido simple */}
          <div className={`VMGP__${theme}-content-wrapper`}>
            {renderPanelContent({
              view: currentView,
              vehicleType: selectedVehicleType,
            })}
          </div>
        </>
      )}

      {/* VISTA DUAL */}
      {viewMode === "dual" && (
        <>
          {/* Navegación dual para vehículos simples */}
          {isSimpleVehicle && (
            <div className={`VMGP__${theme}-dual-navigation simple-vehicle`}>
              <div className={`VMGP__${theme}-dual-section`}>
                <span className={`VMGP__${theme}-dual-label`}>
                  Información del Vehículo
                </span>
                <div className={`VMGP__${theme}-dual-single-panel`}>
                  <span className={`VMGP__${theme}-dual-info-text`}>
                    Datos básicos del vehículo
                  </span>
                </div>
              </div>

              <div className={`VMGP__${theme}-dual-section`}>
                <span className={`VMGP__${theme}-dual-label`}>
                  Documentación del Vehículo
                </span>
                <div className={`VMGP__${theme}-dual-single-panel`}>
                  <span className={`VMGP__${theme}-dual-info-text`}>
                    Documentos del vehículo
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Navegación dual para vehículos con trailer/cabezote */}
          {vehicleRequiresRelated && (
            <div className={`VMGP__${theme}-dual-navigation`}>
              <div className={`VMGP__${theme}-dual-section`}>
                <span className={`VMGP__${theme}-dual-label`}>
                  Información del Vehículo
                </span>
                <div className={`VMGP__${theme}-button-group`}>
                  <button
                    className={`VMGP__${theme}-nav-button ${
                      leftPanel.vehicleType === "main" ? "active" : ""
                    } left`}
                    onClick={() => handleDualNavigation("left", "main")}
                  >
                    Datos <strong>{getCurrentVehicleTypeLabel("main")}</strong>
                  </button>
                  <button
                    className={`VMGP__${theme}-nav-button ${
                      leftPanel.vehicleType === "related" ? "active" : ""
                    } right`}
                    onClick={() => handleDualNavigation("left", "related")}
                  >
                    Datos <strong>{getRelatedVehicleTypeLabel || "N/A"}</strong>
                  </button>
                </div>
              </div>

              <div className={`VMGP__${theme}-dual-section`}>
                <span className={`VMGP__${theme}-dual-label`}>
                  Documentación del Vehículo
                </span>
                <div className={`VMGP__${theme}-button-group`}>
                  <button
                    className={`VMGP__${theme}-nav-button ${
                      rightPanel.vehicleType === "main" ? "active" : ""
                    } left`}
                    onClick={() => handleDualNavigation("right", "main")}
                  >
                    Docs <strong>{getCurrentVehicleTypeLabel("main")}</strong>
                  </button>
                  <button
                    className={`VMGP__${theme}-nav-button ${
                      rightPanel.vehicleType === "related" ? "active" : ""
                    } right`}
                    onClick={() => handleDualNavigation("right", "related")}
                  >
                    Docs <strong>{getRelatedVehicleTypeLabel || "N/A"}</strong>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Contenido dual - SIN CAMBIOS */}
          <div className={`VMGP__${theme}-dual-content-wrapper`}>
            <div className={`VMGP__${theme}-dual-panel left`}>
              {renderPanelContent(leftPanel, "left")}
            </div>
            <div className={`VMGP__${theme}-dual-panel right`}>
              {renderPanelContent(rightPanel, "right")}
            </div>
          </div>
        </>
      )}
      {/* Modales (sin cambios) */}
      <MemoizedPreviewModal
        open={openPreview}
        onClose={() => {
          setOpenPreview(false);
          setSelectedDoc(null);
        }}
        doc={selectedDoc}
        type={"vehicle"}
      />

      <MemoizedMultiDownloadModal
        open={openMultiDownload}
        onClose={() => {
          setOpenMultiDownload(false);
          setDownloadContext({ vehicleType: "main", panelType: "simple" });
          setSelectedDocs([]);
        }}
        personalDocuments={
          prepareDocumentsForDownload(downloadContext.vehicleType).vehicle
        }
        certificates={
          prepareDocumentsForDownload(downloadContext.vehicleType).trailer
        }
        otherDocuments={
          prepareDocumentsForDownload(downloadContext.vehicleType).others
        }
        selectedDocs={selectedDocs}
        setSelectedDocs={setSelectedDocs}
        onDownloadAll={handleDownloadAll}
        userName={(() => {
          const docs = prepareDocumentsForDownload(downloadContext.vehicleType);
          return `${docs.vehicleInfo.plate || "Vehículo"} - ${
            docs.vehicleInfo.type
          }`;
        })()}
        isVehicle={true}
        vehicleContext={{
          type: downloadContext.vehicleType,
          label: getCurrentVehicleTypeLabel(downloadContext.vehicleType),
          panel: downloadContext.panelType,
          vehicleInfo: prepareDocumentsForDownload(downloadContext.vehicleType)
            .vehicleInfo,
        }}
      />

      <MemoizedConfirmDeleteModal
        open={openDeleteDialog}
        onClose={() => {
          setOpenDeleteDialog(false);
          setDocToDelete(null);
        }}
        onConfirm={confirmDeleteDocument}
        title="Eliminar Documento"
        message={`¿Seguro que deseas eliminar el documento "${
          docToDelete?.original_name_description || "seleccionado"
        }"?`}
      />

      {selectedDocumentId && (
        <MemoizedCommentsLocationsDrawer
          open={!!selectedDocumentId}
          onClose={() => selectDocument(null)}
          type="comments"
          data={documentComments?.data || []}
          onEdit={handleUpdateComment}
          onCreate={handleCreateComment}
          onDelete={handleDeleteComment}
          theme={theme}
        />
      )}
    </div>
  );
}

VehicleProfile.propTypes = {
  id: PropTypes.string.isRequired,
};

export default memo(VehicleProfile);
